/**
 * Workshop showcase for PreviewCardFrame and BoardFrame.
 *
 * Realistic mocks plus edge cases: missing image, many cards, empty column,
 * streaming and error states.
 * (Route registration is handled by the main agent — not touched here.)
 */

import { useState } from "react";
import { PreviewCardFrame } from "@/workshop/components/shapes/preview-card-frame/preview-card-frame";
import { BoardFrame } from "@/workshop/components/shapes/board-frame/board-frame";
import type { BoardColumn } from "@/workshop/components/shapes/board-frame/board-frame";

// ─── Mock data ────────────────────────────────────────────────────────────────

const ROLLOUT_COLUMNS: BoardColumn[] = [
  {
    id: "todo",
    label: "To do",
    dotColor: "bg-warning",
    cards: [
      { id: "c1", title: "Token audit for dark mode", meta: "P1 · J. Zhang" },
      { id: "c2", title: "Migrate legacy pills", meta: "v2.4 · A. Rivera" },
    ],
  },
  {
    id: "doing",
    label: "In progress",
    dotColor: "bg-status-selected",
    cards: [
      { id: "c3", title: "Kanban recipe parity", meta: "Blocked by #482 · M. Chen" },
      { id: "c4", title: "Hover pill polish", meta: "A. Rivera" },
    ],
  },
  {
    id: "done",
    label: "Done",
    dotColor: "bg-accent",
    cards: [
      { id: "c5", title: "Surface contract wiring", meta: "v2.3 · J. Zhang" },
    ],
  },
];

const MANY_CARDS_COLUMNS: BoardColumn[] = [
  {
    id: "todo",
    label: "To do",
    dotColor: "bg-warning",
    cards: [
      { id: "m1", title: "Audit icon inventory", meta: "P2 · J. Zhang" },
      { id: "m2", title: "Draft empty-state copy for every frame", meta: "P3 · A. Rivera" },
      { id: "m3", title: "Verify offline fallbacks on slow networks", meta: "P1 · M. Chen" },
      { id: "m4", title: "Long card title that should truncate gracefully in the narrow column layout", meta: "P2 · J. Zhang" },
      { id: "m5", title: "Screenshot review pass", meta: "v2.5 · A. Rivera" },
      { id: "m6", title: "Changelog entry", meta: "M. Chen" },
    ],
  },
  {
    id: "doing",
    label: "In progress",
    dotColor: "bg-status-selected",
    cards: [
      { id: "m7", title: "Streaming skeleton states", meta: "Blocked by #511 · J. Zhang" },
    ],
  },
  {
    id: "done",
    label: "Done",
    dotColor: "bg-accent",
    cards: [
      { id: "m8", title: "Frame contract checklist", meta: "v2.3 · M. Chen" },
      { id: "m9", title: "Picsum fallback tiles", meta: "v2.3 · A. Rivera" },
    ],
  },
];

const EMPTY_COLUMN_COLUMNS: BoardColumn[] = [
  {
    id: "todo",
    label: "To do",
    dotColor: "bg-warning",
    cards: [{ id: "e1", title: "Only card left", meta: "P1 · J. Zhang" }],
  },
  {
    id: "doing",
    label: "In progress",
    dotColor: "bg-status-selected",
    cards: [],
  },
  {
    id: "done",
    label: "Done",
    dotColor: "bg-accent",
    cards: [],
  },
];

// ─── Section helper ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10" data-testid={`preview-board-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap items-start gap-6">{children}</div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PreviewBoardFramesPage() {
  const [lastAction, setLastAction] = useState<string | null>(null);

  return (
    <div className="workshop-canvas-bg min-h-full p-8" data-testid="preview-board-frames-page">
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Preview Card · Board Snapshot</h1>
        <p className="mt-1 text-sm text-white/40">
          Rich hero-image preview and mini kanban board frames. Cards and
          Open buttons report actions locally.
        </p>
        {lastAction && (
          <p className="mt-2 text-xs text-white/50" data-testid="preview-board-frames-last-action">
            Action: {lastAction}
          </p>
        )}
      </div>

      <Section title="Happy path">
        <div className="w-[360px]">
          <PreviewCardFrame
            data-testid="preview-card-frame-demo"
            imageUrl="https://picsum.photos/seed/aman-tokyo/640/400"
            title="Aman Tokyo — Retreat in Otemachi"
            source="Preview · aman-tokyo.lovable.app"
            onOpen={(target) => setLastAction(`Opened ${target}`)}
          />
        </div>
        <div className="w-[420px]">
          <BoardFrame
            data-testid="board-frame-demo"
            title="Design System — rollout"
            path="Design / Neutral Base"
            columns={ROLLOUT_COLUMNS}
            onCardClick={(cardId) => setLastAction(`Card ${cardId}`)}
          />
        </div>
      </Section>

      <Section title="Preview edge cases">
        <div className="w-[360px]">
          <PreviewCardFrame
            data-testid="preview-card-frame-no-image"
            imageUrl=""
            title="Offline cabin brief"
            source="Preview · local draft"
          />
        </div>
        <div className="w-[360px]">
          <PreviewCardFrame
            data-testid="preview-card-frame-broken-image"
            imageUrl="https://invalid.example/hero-missing.jpg"
            title="Broken hero falls back gracefully"
            source="Preview · broken.example"
          />
        </div>
        <div className="w-[360px]">
          <PreviewCardFrame
            data-testid="preview-card-frame-streaming"
            title="Streaming preview"
            source="Preview · loading…"
            status="streaming"
          />
        </div>
        <div className="w-[360px]">
          <PreviewCardFrame
            data-testid="preview-card-frame-error"
            title="Failed preview"
            source="Preview · unreachable"
            status="error"
          />
        </div>
      </Section>

      <Section title="Board edge cases">
        <div className="w-[420px]">
          <BoardFrame
            data-testid="board-frame-many-cards"
            title="Launch checklist — full backlog"
            path="Ops / Release v2.5"
            columns={MANY_CARDS_COLUMNS}
            onCardClick={(cardId) => setLastAction(`Card ${cardId}`)}
          />
        </div>
        <div className="w-[420px]">
          <BoardFrame
            data-testid="board-frame-empty-column"
            title="Sprint — mid-week"
            path="Eng / Sprint 12"
            columns={EMPTY_COLUMN_COLUMNS}
          />
        </div>
        <div className="w-[420px]">
          <BoardFrame
            data-testid="board-frame-streaming"
            title="Loading board"
            path="Design / Neutral Base"
            columns={[]}
            status="streaming"
          />
        </div>
        <div className="w-[420px]">
          <BoardFrame
            data-testid="board-frame-error"
            title="Unreachable board"
            columns={ROLLOUT_COLUMNS}
            status="error"
          />
        </div>
      </Section>
    </div>
  );
}
